'use client';

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type PropsWithChildren,
    type ReactElement,
} from 'react';
import { mapProductGiftToLineItemAttributes, type ProductGiftDetails } from '@/lib/product-gift-schemas';
import type { SCAPIShopperBasketsV2Schemas } from '@/types/scapi';

type ProductGiftLineItemAttributes = Partial<
    Pick<
        SCAPIShopperBasketsV2Schemas['ProductItem'],
        'c_isGift' | 'c_giftRecipientName' | 'c_giftRecipientEmail' | 'c_giftRecipientPhone' | 'c_giftMessage'
    >
>;

interface ProductGiftContextValue {
    giftDetails: ProductGiftDetails | null;
    isDrawerOpen: boolean;
    isGiftEnabled: boolean;
    saveGiftDetails: (details: ProductGiftDetails) => void;
    clearGiftDetails: () => void;
    openDrawer: () => void;
    closeDrawer: () => void;
    getLineItemAttributes: () => ProductGiftLineItemAttributes | undefined;
}

const ProductGiftContext = createContext<ProductGiftContextValue | null>(null);

export function ProductGiftProvider({ children }: PropsWithChildren): ReactElement {
    const [giftDetails, setGiftDetails] = useState<ProductGiftDetails | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const saveGiftDetails = useCallback((details: ProductGiftDetails) => {
        setGiftDetails(details);
        setIsDrawerOpen(false);
    }, []);

    const clearGiftDetails = useCallback(() => {
        setGiftDetails(null);
        setIsDrawerOpen(false);
    }, []);

    const openDrawer = useCallback(() => {
        setIsDrawerOpen(true);
    }, []);

    const closeDrawer = useCallback(() => {
        setIsDrawerOpen(false);
    }, []);

    const getLineItemAttributes = useCallback((): ProductGiftLineItemAttributes | undefined => {
        if (!giftDetails) {
            return undefined;
        }

        return mapProductGiftToLineItemAttributes(giftDetails);
    }, [giftDetails]);

    const value = useMemo(
        () => ({
            giftDetails,
            isDrawerOpen,
            isGiftEnabled: giftDetails !== null,
            saveGiftDetails,
            clearGiftDetails,
            openDrawer,
            closeDrawer,
            getLineItemAttributes,
        }),
        [giftDetails, isDrawerOpen, saveGiftDetails, clearGiftDetails, openDrawer, closeDrawer, getLineItemAttributes]
    );

    return <ProductGiftContext.Provider value={value}>{children}</ProductGiftContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useProductGift(): ProductGiftContextValue {
    const context = useContext(ProductGiftContext);
    if (!context) {
        throw new Error('useProductGift must be used within ProductGiftProvider');
    }
    return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalProductGift(): ProductGiftContextValue | null {
    return useContext(ProductGiftContext);
}
